import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useListUiState } from '../hooks/useListUiState'
import { useScrollRestoration } from '../hooks/useScrollRestoration'
import { useAuth } from '../contexts/AuthContext'
import {
  getAssignmentsByTeacherPage,
  getAssignmentsCountByTeacher,
  updateAssignment,
  deleteAssignment,
  deleteAssignmentsForTeacherClass
} from '../services/assignmentService'
import { getTeacherClasses } from '../services/teacherClassService'
import { getAttemptsByAssignment } from '../services/attemptService'
import { getStudentsByTeacherClass } from '../services/userService'
import {
  getOrCreateExerciseShareCode,
  getExercisesByAuthor,
  getVideoRanksForExercises,
  getVideoRanksForExerciseIds
} from '../services/exerciseService'
import './TeacherAssignments.css'

const PAGE_SIZE = 20
const SORT_KEYS = [
  { id: 'dueDate', label: 'Date limite' },
  { id: 'createdAt', label: 'Date de création' },
  { id: 'className', label: 'Classe' },
  { id: 'submissionRate', label: 'Taux de rendu' }
]
const GROUP_OPTIONS = [
  { id: 'none', label: 'Aucun' },
  { id: 'class', label: 'Par classe' },
  { id: 'period', label: 'Par période' }
]
const PERIOD_LABELS = { week: 'Cette semaine', month: 'Ce mois', older: 'Plus ancien' }

function formatDate(timestamp) {
  if (!timestamp) return '—'
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date)
}

function getWeekStart(d) {
  const date = new Date(d)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(date.setDate(diff))
}

function getPeriodKey(a) {
  const ts = a.dueDate ? (a.dueDate.toDate ? a.dueDate.toDate() : new Date(a.dueDate)) : (a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0))
  const now = new Date()
  const weekStart = getWeekStart(new Date(now))
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  if (ts >= weekStart) return 'week'
  if (ts >= monthStart) return 'month'
  return 'older'
}


const ASSIGNMENTS_LIST_FIELDS = {
  classId: { type: 'string', default: '' },
  status: { type: 'string', default: 'all' },
  sort: { type: 'string', default: 'dueDate' },
  order: { type: 'string', default: 'asc' },
  group: { type: 'string', default: 'none' },
  view: { type: 'string', default: 'cards' },
  page: { type: 'number', default: 1 },
}

function TeacherAssignments() {
  const { user, userData } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  useScrollRestoration('teacher-assignments', { ready: !loading })
  const [assignments, setAssignments] = useState([])
  const [firstDoc, setFirstDoc] = useState(null)
  const [lastDoc, setLastDoc] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingCount, setLoadingCount] = useState(false)
  const [classNames, setClassNames] = useState({})
  const [classesList, setClassesList] = useState([])
  const [assignmentStats, setAssignmentStats] = useState({})
  const [shareCodes, setShareCodes] = useState({})
  const [loadingStats, setLoadingStats] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editInstructions, setEditInstructions] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [expandedAssignmentId, setExpandedAssignmentId] = useState(null)
  const {
    values: listValues,
    setField: setListField,
  } = useListUiState({
    fields: ASSIGNMENTS_LIST_FIELDS,
    resetPageKey: 'page',
  })
  const filterClass = listValues.classId
  const setFilterClass = (v) => setListField('classId', v)
  const filterStatus = listValues.status
  const setFilterStatus = (v) => setListField('status', v)
  const sortKey = listValues.sort
  const setSortKey = (v) => setListField('sort', v)
  const sortOrder = listValues.order
  const setSortOrder = (v) => setListField('order', typeof v === 'function' ? v(listValues.order) : v)
  const groupBy = listValues.group
  const setGroupBy = (v) => setListField('group', v)
  const viewMode = listValues.view
  const setViewMode = (v) => setListField('view', v)
  const currentPage = listValues.page
  const setCurrentPage = (v) => setListField('page', typeof v === 'function' ? v(listValues.page) : v)
  const [globalVideoRanksMap, setGlobalVideoRanksMap] = useState(() => new Map())
  const [pageVideoRanksMap, setPageVideoRanksMap] = useState(() => new Map())
  const [clearingClassAssignments, setClearingClassAssignments] = useState(false)

  const loadClasses = useCallback(async () => {
    if (!user) return
    const classes = await getTeacherClasses(user.uid)
    const map = {}
    classes.forEach((c) => { map[c.id] = c.name })
    setClassNames(map)
    setClassesList(classes)
  }, [user])

  const loadPage = useCallback(async (opts = {}) => {
    if (!user) return
    const { startAfterDoc = null, endBeforeDoc = null } = opts
    setLoading(true)
    try {
      const result = await getAssignmentsByTeacherPage(user.uid, {
        pageSize: PAGE_SIZE,
        startAfterDoc,
        endBeforeDoc
      })
      setAssignments(result.assignments)
      setFirstDoc(result.firstDoc)
      setLastDoc(result.lastDoc)
      setHasMore(result.hasMore)
    } catch (err) {
      console.error('TeacherAssignments loadPage:', err)
      setAssignments([])
    } finally {
      setLoading(false)
    }
  }, [user])

  const loadTotalCount = useCallback(async () => {
    if (!user) return
    setLoadingCount(true)
    try {
      const count = await getAssignmentsCountByTeacher(user.uid)
      setTotalCount(count)
    } catch (err) {
      console.error('TeacherAssignments loadCount:', err)
      setTotalCount(null)
    } finally {
      setLoadingCount(false)
    }
  }, [user])

  useEffect(() => {
    loadClasses()
  }, [loadClasses])

  useEffect(() => {
    if (!user) return
    loadPage()
    loadTotalCount()
  }, [user])

  const handleClearAssignmentsForFilteredClass = async () => {
    if (!user?.uid || !filterClass) return
    const name = classNames[filterClass] || 'cette classe'
    if (
      !window.confirm(
        `Supprimer tous les devoirs de « ${name} » ? Les tentatives des élèves peuvent rester sans lien vers le devoir.`
      )
    ) {
      return
    }
    setClearingClassAssignments(true)
    try {
      const n = await deleteAssignmentsForTeacherClass(user.uid, filterClass)
      setCurrentPage(1)
      await loadPage()
      await loadTotalCount()
      alert(n > 0 ? `${n} devoir${n > 1 ? 's' : ''} supprimé${n > 1 ? 's' : ''}.` : 'Aucun devoir à supprimer.')
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression des devoirs.')
    } finally {
      setClearingClassAssignments(false)
    }
  }

  const goToNextPage = () => {
    if (!hasMore || !lastDoc) return
    setCurrentPage((p) => p + 1)
    loadPage({ startAfterDoc: lastDoc })
  }

  const goToPrevPage = () => {
    if (currentPage <= 1 || !firstDoc) return
    setCurrentPage((p) => Math.max(1, p - 1))
    loadPage({ endBeforeDoc: firstDoc })
  }

  const loadStats = useCallback(async () => {
    if (!user || assignments.length === 0) {
      setAssignmentStats({})
      return
    }
    setLoadingStats(true)
    try {
      const uniqueClassIds = Array.from(new Set(assignments.map((a) => a.teacherClassId).filter(Boolean)))
      const classEntries = await Promise.all(
        uniqueClassIds.map(async (classId) => [classId, await getStudentsByTeacherClass(classId)])
      )
      const classMap = Object.fromEntries(classEntries)

      const statsEntries = await Promise.all(
        assignments.map(async (assignment) => {
          const classList = classMap[assignment.teacherClassId] || []
          const classStudentIds = new Set(classList.map((s) => s.id))
          const attempts = await getAttemptsByAssignment(assignment.id, 600)
          const latestByStudent = new Map()
          attempts.forEach((attempt) => {
            const userId = attempt.userId
            if (!classStudentIds.has(userId)) return
            const prev = latestByStudent.get(userId)
            const prevTs = prev?.completedAt?.toDate?.()?.getTime?.() ?? 0
            const currTs = attempt?.completedAt?.toDate?.()?.getTime?.() ?? 0
            if (!prev || currTs >= prevTs) latestByStudent.set(userId, attempt)
          })

          const submittedCount = latestByStudent.size
          const averageScore = submittedCount > 0
            ? Math.round(Array.from(latestByStudent.values()).reduce((sum, a) => sum + (a.score || 0), 0) / submittedCount)
            : 0
          const dueDate = assignment.dueDate ? (assignment.dueDate.toDate ? assignment.dueDate.toDate() : new Date(assignment.dueDate)) : null
          const isExpired = !!dueDate && dueDate.getTime() < Date.now()
          const missingCount = Math.max(0, classList.length - submittedCount)
          const overdueCount = isExpired ? missingCount : 0

          const studentRows = classList.map((student) => {
            const latest = latestByStudent.get(student.id) || null
            const score = latest?.score ?? null
            const done = !!latest
            return {
              studentId: student.id,
              studentName: student.displayName || student.email || 'Élève',
              done,
              score,
              status: done ? 'done' : (isExpired ? 'overdue' : 'todo'),
              completedAt: latest?.completedAt || null
            }
          })

          return [assignment.id, {
            classSize: classList.length,
            submittedCount,
            missingCount,
            overdueCount,
            averageScore,
            studentRows
          }]
        })
      )

      setAssignmentStats(Object.fromEntries(statsEntries))
    } catch (error) {
      console.error('TeacherAssignments loadStats:', error)
      setAssignmentStats({})
    } finally {
      setLoadingStats(false)
    }
  }, [assignments, user])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  useEffect(() => {
    if (!user?.uid) {
      setGlobalVideoRanksMap(new Map())
      return
    }
    getExercisesByAuthor(user.uid)
      .then((allMine) => allMine)
      .then((allMine) => getVideoRanksForExercises(allMine))
      .then((map) => setGlobalVideoRanksMap(map))
      .catch(() => setGlobalVideoRanksMap(new Map()))
  }, [user?.uid])

  useEffect(() => {
    if (!assignments.length) {
      setPageVideoRanksMap(new Map())
      return
    }
    const exerciseIds = []
    assignments.forEach((a) => {
      const items = Array.isArray(a.items) && a.items.length > 0 ? a.items : [{ exerciseId: a.exerciseId }]
      items.forEach((item) => {
        if (item.exerciseId) exerciseIds.push(item.exerciseId)
      })
    })
    const ids = [...new Set(exerciseIds)]
    getVideoRanksForExerciseIds(ids).then((map) => {
      setPageVideoRanksMap(map)
    }).catch(() => setPageVideoRanksMap(new Map()))
  }, [assignments])

  useEffect(() => {
    if (!assignments.length) return
    const ids = []
    assignments.forEach((a) => {
      const items = Array.isArray(a.items) && a.items.length > 0 ? a.items : [{ exerciseId: a.exerciseId }]
      items.forEach((item) => { if (item.exerciseId) ids.push(item.exerciseId) })
    })
    const uniqueIds = [...new Set(ids)].slice(0, 12)
    const diagnostics = uniqueIds.map((exerciseId) => {
      const global = globalVideoRanksMap.get(exerciseId)
      const page = pageVideoRanksMap.get(exerciseId)
      return {
        exerciseId,
        globalGroupSize: global?.groupSize ?? null,
        pageGroupSize: page?.groupSize ?? null,
        selectedGroupSize: (global || page)?.groupSize ?? null
      }
    })
  }, [assignments, globalVideoRanksMap, pageVideoRanksMap])

  useEffect(() => {
    if (!user || userData?.role !== 'teacher') {
      navigate('/dashboard')
      return
    }
  }, [user, userData?.role, navigate])

  const getAssignmentStatus = (a) => {
    const stats = assignmentStats[a.id] || {}
    const dueDate = a.dueDate ? (a.dueDate.toDate ? a.dueDate.toDate() : new Date(a.dueDate)) : null
    const isExpired = !!dueDate && dueDate.getTime() < Date.now()
    const missingCount = stats.missingCount ?? 0
    if (isExpired && missingCount > 0) return 'overdue'
    if (isExpired) return 'ended'
    if (dueDate && dueDate.getTime() > Date.now()) return 'upcoming'
    return 'active'
  }

  const filteredAssignments = useMemo(() => {
    return assignments.filter((a) => {
      if (filterClass && a.teacherClassId !== filterClass) return false
      if (filterStatus !== 'all' && getAssignmentStatus(a) !== filterStatus) return false
      return true
    })
  }, [assignments, filterClass, filterStatus, assignmentStats])

  const sortedAssignments = useMemo(() => {
    const mult = sortOrder === 'asc' ? 1 : -1
    return [...filteredAssignments].sort((a, b) => {
      const statsA = assignmentStats[a.id] || {}
      const statsB = assignmentStats[b.id] || {}
      const rateA = statsA.classSize ? (statsA.submittedCount / statsA.classSize) * 100 : 0
      const rateB = statsB.classSize ? (statsB.submittedCount / statsB.classSize) * 100 : 0
      const dueA = a.dueDate ? (a.dueDate.toDate ? a.dueDate.toDate() : new Date(a.dueDate)).getTime() : 0
      const dueB = b.dueDate ? (b.dueDate.toDate ? b.dueDate.toDate() : new Date(b.dueDate)).getTime() : 0
      const createdA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)).getTime() : 0
      const createdB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)).getTime() : 0
      const nameA = (classNames[a.teacherClassId] || '').toLowerCase()
      const nameB = (classNames[b.teacherClassId] || '').toLowerCase()
      let cmp = 0
      switch (sortKey) {
        case 'dueDate': cmp = dueA - dueB; break
        case 'createdAt': cmp = createdA - createdB; break
        case 'className': cmp = nameA.localeCompare(nameB); break
        case 'submissionRate': cmp = rateA - rateB; break
        default: break
      }
      return cmp * mult
    })
  }, [filteredAssignments, sortKey, sortOrder, classNames, assignmentStats])

  const groupedAssignments = useMemo(() => {
    if (groupBy === 'none') return [{ key: null, label: null, items: sortedAssignments }]
    if (groupBy === 'class') {
      const byClass = new Map()
      sortedAssignments.forEach((a) => {
        const k = a.teacherClassId || ''
        if (!byClass.has(k)) byClass.set(k, [])
        byClass.get(k).push(a)
      })
      return Array.from(byClass.entries()).map(([key, items]) => ({
        key,
        label: classNames[key] || key || 'Sans classe',
        items
      }))
    }
    if (groupBy === 'period') {
      const byPeriod = new Map()
      sortedAssignments.forEach((a) => {
        const k = getPeriodKey(a)
        if (!byPeriod.has(k)) byPeriod.set(k, [])
        byPeriod.get(k).push(a)
      })
      const order = ['week', 'month', 'older']
      return order.map((k) => ({ key: k, label: PERIOD_LABELS[k], items: byPeriod.get(k) || [] })).filter((g) => g.items.length > 0)
    }
    return [{ key: null, label: null, items: sortedAssignments }]
  }, [sortedAssignments, groupBy, classNames])

  const filterCounts = useMemo(() => {
    const statusCounts = { all: assignments.length, upcoming: 0, active: 0, ended: 0, overdue: 0 }
    const classCounts = { '': assignments.length }
    assignments.forEach((a) => {
      const status = getAssignmentStatus(a)
      statusCounts[status] = (statusCounts[status] ?? 0) + 1
      const c = a.teacherClassId || ''
      classCounts[c] = (classCounts[c] ?? 0) + 1
    })
    return { statusCounts, classCounts }
  }, [assignments, assignmentStats])

  const totalPages = totalCount != null ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : null
  const fromTo = totalCount != null ? `${(currentPage - 1) * PAGE_SIZE + 1}-${Math.min(currentPage * PAGE_SIZE, totalCount)}` : ''

  const startEdit = (a) => {
    setEditingId(a.id)
    setEditTitle(a.title || '')
    setEditInstructions(a.instructions || '')
    setEditDueDate(a.dueDate ? (a.dueDate.toDate ? a.dueDate.toDate() : new Date(a.dueDate)).toISOString().slice(0, 10) : '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditTitle('')
    setEditInstructions('')
    setEditDueDate('')
  }

  const saveEdit = async () => {
    if (!editingId || !user) return
    setSaving(true)
    try {
      await updateAssignment(editingId, user.uid, {
        title: editTitle.trim() || null,
        instructions: editInstructions.trim() || null,
        dueDate: editDueDate ? new Date(editDueDate + 'T23:59:59') : null
      })
      setAssignments((prev) =>
        prev.map((a) =>
          a.id === editingId
            ? {
                ...a,
                title: editTitle.trim() || null,
                instructions: editInstructions.trim() || null,
                dueDate: editDueDate ? new Date(editDueDate + 'T23:59:59') : null
              }
            : a
        )
      )
      cancelEdit()
    } catch (err) {
      alert(err.message || 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (assignmentId) => {
    if (!window.confirm('Supprimer ce devoir ?')) return
    setDeletingId(assignmentId)
    try {
      await deleteAssignment(assignmentId, user.uid)
      setAssignments((prev) => prev.filter((a) => a.id !== assignmentId))
      if (totalCount != null) setTotalCount((c) => Math.max(0, c - 1))
    } catch (err) {
      alert(err.message || 'Erreur')
    } finally {
      setDeletingId(null)
    }
  }

  const copyShareCode = async (exerciseId) => {
    if (!exerciseId) return
    try {
      let code = shareCodes[exerciseId]
      if (!code) {
        const created = await getOrCreateExerciseShareCode(exerciseId)
        code = created.code
        setShareCodes((prev) => ({ ...prev, [exerciseId]: code }))
      }
      await navigator.clipboard.writeText(code)
      alert(`Code copié : ${code}`)
    } catch (error) {
      alert(error.message || 'Impossible de générer/copier le code.')
    }
  }

  const toggleSort = (key) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else setSortKey(key)
  }

  const renderCard = (a) => {
    const stats = assignmentStats[a.id] || { classSize: 0, submittedCount: 0, missingCount: 0, overdueCount: 0, averageScore: 0, studentRows: [] }
    const isExpanded = expandedAssignmentId === a.id
    const items = Array.isArray(a.items) && a.items.length > 0
      ? a.items
      : [{ exerciseId: a.exerciseId, exerciseTitle: a.exerciseTitle || 'Exercice', exerciseThumbnail: a.exerciseThumbnail ?? null }]
    const itemCount = items.length
    return (
      <li key={a.id} className="teacher-assignments-card">
        <div className="teacher-assignments-card-main">
          {/* Miniatures des exercices avec badge 1/X (tous les exercices Opus Lab avec la même vidéo, affiché seulement à partir de 2) */}
          <div className="teacher-assignments-thumbnails">
            {items.map((item, index) => {
              const thumbUrl = item.exerciseThumbnail || null
              const videoRank = globalVideoRanksMap.get(item.exerciseId) || pageVideoRanksMap.get(item.exerciseId)
              const showBadge = videoRank && videoRank.groupSize >= 2
              return (
                <button
                  key={item.exerciseId + '-' + index}
                  type="button"
                  className="teacher-assignments-thumbnail-wrap"
                  onClick={() => navigate(`/play/${item.exerciseId}`, { state: { assignmentId: a.id, returnTo: `${location.pathname}${location.search || ''}` } })}
                  title={item.exerciseTitle ? `${item.exerciseTitle}${showBadge ? ` (${videoRank.rank}/${videoRank.groupSize} pour cette vidéo)` : ''}` : 'Exercice'}
                >
                  {showBadge && (
                    <span className="teacher-assignments-thumbnail-badge" aria-label={`Exercice ${videoRank.rank} sur ${videoRank.groupSize} (même vidéo)`}>
                      {videoRank.rank}/{videoRank.groupSize}
                    </span>
                  )}
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt=""
                      className="teacher-assignments-thumbnail-img"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        const placeholder = e.target.nextElementSibling
                        if (placeholder) placeholder.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <div
                    className="teacher-assignments-thumbnail-placeholder"
                    style={{ display: thumbUrl ? 'none' : 'flex' }}
                    aria-hidden="true"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                  </div>
                </button>
              )
            })}
          </div>
          <h3 className="teacher-assignments-card-title">
            {editingId === a.id ? (
              <input
                type="text"
                className="teacher-assignments-edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Titre du devoir"
              />
            ) : (
              a.title || a.exerciseTitle || 'Exercice'
            )}
          </h3>
          {editingId === a.id ? (
            <textarea
              className="teacher-assignments-edit-title"
              value={editInstructions}
              onChange={(e) => setEditInstructions(e.target.value)}
              placeholder="Consigne (optionnelle)"
              rows={2}
            />
          ) : (
            a.instructions ? <p className="teacher-assignments-card-date">{a.instructions}</p> : null
          )}
          <p className="teacher-assignments-card-class">{classNames[a.teacherClassId] || a.teacherClassId}</p>
          <p className="teacher-assignments-card-date">
            Limite : {editingId === a.id ? (
              <input
                type="date"
                className="teacher-assignments-edit-date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
              />
            ) : (
              formatDate(a.dueDate)
            )}
          </p>
          <p className="teacher-assignments-card-date">
            Exercices : {itemCount}
            {loadingStats ? ' • Calcul en cours…' : ` • Rendus ${stats.submittedCount}/${stats.classSize} • Moyenne ${stats.averageScore}%`}
          </p>
          {stats.overdueCount > 0 && (
            <p className="teacher-assignments-card-date">En retard : {stats.overdueCount}</p>
          )}
        </div>
        <div className="teacher-assignments-card-actions">
          {editingId === a.id ? (
            <>
              <button type="button" onClick={saveEdit} disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
              <button type="button" onClick={cancelEdit} disabled={saving}>Annuler</button>
            </>
          ) : (
            <>
              <button type="button" className="teacher-assignments-btn-play" onClick={() => navigate(`/play/${a.exerciseId}`, { state: { assignmentId: a.id, returnTo: `${location.pathname}${location.search || ''}` } })}>
                Voir l'exercice
              </button>
              <button type="button" onClick={() => copyShareCode(a.exerciseId)}>Code court</button>
              <button type="button" onClick={() => setExpandedAssignmentId(isExpanded ? null : a.id)}>
                {isExpanded ? 'Masquer résultats' : 'Voir résultats'}
              </button>
              <button type="button" onClick={() => startEdit(a)}>Modifier</button>
              <button type="button" className="danger" onClick={() => handleDelete(a.id)} disabled={deletingId === a.id}>
                {deletingId === a.id ? '…' : 'Supprimer'}
              </button>
            </>
          )}
        </div>
        {isExpanded && (
          <div className="teacher-assignments-card-main teacher-assignments-card-main--expanded">
            <h4 className="teacher-assignments-card-title" style={{ fontSize: '1rem' }}>Résultats élèves</h4>
            {stats.studentRows.length === 0 ? (
              <p className="teacher-assignments-card-date">Aucun élève rattaché à cette classe.</p>
            ) : (
              <ul className="teacher-assignments-list teacher-assignments-list--nested">
                {stats.studentRows.map((row) => (
                  <li key={row.studentId} className="teacher-assignments-card teacher-assignments-card--nested">
                    <div className="teacher-assignments-card-main">
                      <h5 className="teacher-assignments-card-title" style={{ fontSize: '0.95rem' }}>{row.studentName}</h5>
                      <p className="teacher-assignments-card-date">
                        {row.status === 'done' ? `Rendu • ${row.score ?? 0}%` : row.status === 'overdue' ? 'Non rendu (retard)' : 'À faire'}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </li>
    )
  }

  if (!user || userData?.role !== 'teacher') return null

  return (
    <div className="teacher-assignments-page">
      <header className="teacher-assignments-header">
        <button type="button" className="teacher-assignments-back" onClick={() => navigate('/dashboard')}>
          ← Retour au tableau de bord
        </button>
        <h1 className="teacher-assignments-title">Devoirs</h1>
        <p className="teacher-assignments-intro">
          Vue de pilotage : suivi des rendus, retards et progression par devoir.
        </p>
      </header>

      {loading && assignments.length === 0 ? (
        <div className="teacher-assignments-loading">
          <div className="spinner" />
          <p>Chargement des devoirs…</p>
        </div>
      ) : assignments.length === 0 ? (
        <div className="teacher-assignments-empty">
          <p>Aucun devoir pour le moment.</p>
          <p>Depuis le tableau de bord, ouvrez le menu d'un exercice et choisissez « Assigner à une classe ».</p>
        </div>
      ) : (
        <>
          <div className="teacher-assignments-toolbar">
            <div className="teacher-assignments-filters">
              <label className="teacher-assignments-filter-label">Classe</label>
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="teacher-assignments-select"
              >
                <option value="">Toutes ({filterCounts.classCounts[''] ?? 0})</option>
                {classesList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({filterCounts.classCounts[c.id] ?? 0})</option>
                ))}
              </select>
              {filterClass && (
                <button
                  type="button"
                  className="teacher-assignments-clear-class-btn"
                  onClick={handleClearAssignmentsForFilteredClass}
                  disabled={clearingClassAssignments}
                  title="Supprimer tous les devoirs de la classe sélectionnée"
                >
                  {clearingClassAssignments ? 'Suppression…' : 'Vider les devoirs de cette classe'}
                </button>
              )}
              <label className="teacher-assignments-filter-label">Statut</label>
              <div className="teacher-assignments-filter-buttons">
                {[
                  ['all', `Tous (${filterCounts.statusCounts.all})`],
                  ['upcoming', `À venir (${filterCounts.statusCounts.upcoming})`],
                  ['active', `En cours (${filterCounts.statusCounts.active})`],
                  ['ended', `Terminés (${filterCounts.statusCounts.ended})`],
                  ['overdue', `En retard (${filterCounts.statusCounts.overdue})`]
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`teacher-assignments-filter-btn ${filterStatus === value ? 'is-active' : ''}`}
                    onClick={() => setFilterStatus(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="teacher-assignments-sort-group">
              <label className="teacher-assignments-filter-label">Tri</label>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="teacher-assignments-select"
              >
                {SORT_KEYS.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="teacher-assignments-sort-order"
                onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                title={sortOrder === 'asc' ? 'Croissant (cliquer pour décroissant)' : 'Décroissant (cliquer pour croissant)'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
            <div className="teacher-assignments-group-view">
              <label className="teacher-assignments-filter-label">Regrouper</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="teacher-assignments-select"
              >
                {GROUP_OPTIONS.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
              <span className="teacher-assignments-view-toggle">
                <button
                  type="button"
                  className={viewMode === 'cards' ? 'is-active' : ''}
                  onClick={() => setViewMode('cards')}
                >
                  Cartes
                </button>
                <button
                  type="button"
                  className={viewMode === 'table' ? 'is-active' : ''}
                  onClick={() => setViewMode('table')}
                >
                  Tableau
                </button>
              </span>
            </div>
          </div>

          {filteredAssignments.length === 0 ? (
            <p className="teacher-assignments-empty-inline">Aucun devoir dans ce filtre.</p>
          ) : viewMode === 'table' ? (
            <div className="teacher-assignments-table-wrap">
              <table className="teacher-assignments-table">
                <thead>
                  <tr>
                    <th><button type="button" className="teacher-assignments-th-btn" onClick={() => toggleSort('dueDate')}>Titre</button></th>
                    <th><button type="button" className="teacher-assignments-th-btn" onClick={() => toggleSort('className')}>Classe {sortKey === 'className' && (sortOrder === 'asc' ? '↑' : '↓')}</button></th>
                    <th><button type="button" className="teacher-assignments-th-btn" onClick={() => toggleSort('dueDate')}>Date limite {sortKey === 'dueDate' && (sortOrder === 'asc' ? '↑' : '↓')}</button></th>
                    <th><button type="button" className="teacher-assignments-th-btn" onClick={() => toggleSort('submissionRate')}>Rendus {sortKey === 'submissionRate' && (sortOrder === 'asc' ? '↑' : '↓')}</button></th>
                    <th>Moyenne</th>
                    <th>Retards</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedAssignments.flatMap((grp) =>
                    grp.label ? [
                      <tr key={`group-${grp.key}`} className="teacher-assignments-table-group-row">
                        <td colSpan={7}>{grp.label}</td>
                      </tr>,
                      ...grp.items.flatMap((a) => {
                        const stats = assignmentStats[a.id] || {}
                        const rate = stats.classSize ? Math.round((stats.submittedCount / stats.classSize) * 100) : 0
                        const isExpanded = expandedAssignmentId === a.id
                        return [
                          <tr key={a.id}>
                            <td>{a.title || a.exerciseTitle || 'Exercice'}</td>
                            <td>{classNames[a.teacherClassId] || a.teacherClassId}</td>
                            <td>{formatDate(a.dueDate)}</td>
                            <td>{loadingStats ? '…' : `${stats.submittedCount}/${stats.classSize} (${rate}%)`}</td>
                            <td>{loadingStats ? '…' : `${stats.averageScore ?? 0}%`}</td>
                            <td>{stats.overdueCount > 0 ? stats.overdueCount : '—'}</td>
                            <td>
                              <button type="button" className="teacher-assignments-btn-play" onClick={() => navigate(`/play/${a.exerciseId}`, { state: { assignmentId: a.id, returnTo: `${location.pathname}${location.search || ''}` } })}>Voir</button>
                              <button type="button" onClick={() => copyShareCode(a.exerciseId)}>Code</button>
                              <button type="button" onClick={() => setExpandedAssignmentId(isExpanded ? null : a.id)}>{isExpanded ? 'Masquer' : 'Résultats'}</button>
                              <button type="button" onClick={() => startEdit(a)}>Modifier</button>
                              <button type="button" className="danger" onClick={() => handleDelete(a.id)} disabled={deletingId === a.id}>{deletingId === a.id ? '…' : 'Suppr.'}</button>
                            </td>
                          </tr>,
                          isExpanded && (
                            <tr key={`${a.id}-detail`} className="teacher-assignments-table-detail-row">
                              <td colSpan={7}>
                                <div className="teacher-assignments-table-detail">
                                  <strong>Résultats élèves</strong>
                                  {stats.studentRows?.length === 0 ? (
                                    <p>Aucun élève rattaché à cette classe.</p>
                                  ) : (
                                    <ul>
                                      {(stats.studentRows || []).map((row) => (
                                        <li key={row.studentId}>
                                          {row.studentName} — {row.status === 'done' ? `Rendu • ${row.score ?? 0}%` : row.status === 'overdue' ? 'Non rendu (retard)' : 'À faire'}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        ].filter(Boolean)
                      })
                    ] : grp.items.flatMap((a) => {
                      const stats = assignmentStats[a.id] || {}
                      const rate = stats.classSize ? Math.round((stats.submittedCount / stats.classSize) * 100) : 0
                      const isExpanded = expandedAssignmentId === a.id
                      return [
                        <tr key={a.id}>
                          <td>{a.title || a.exerciseTitle || 'Exercice'}</td>
                          <td>{classNames[a.teacherClassId] || a.teacherClassId}</td>
                          <td>{formatDate(a.dueDate)}</td>
                          <td>{loadingStats ? '…' : `${stats.submittedCount}/${stats.classSize} (${rate}%)`}</td>
                          <td>{loadingStats ? '…' : `${stats.averageScore ?? 0}%`}</td>
                          <td>{stats.overdueCount > 0 ? stats.overdueCount : '—'}</td>
                          <td>
                            <button type="button" className="teacher-assignments-btn-play" onClick={() => navigate(`/play/${a.exerciseId}`, { state: { assignmentId: a.id, returnTo: `${location.pathname}${location.search || ''}` } })}>Voir</button>
                            <button type="button" onClick={() => copyShareCode(a.exerciseId)}>Code</button>
                            <button type="button" onClick={() => setExpandedAssignmentId(isExpanded ? null : a.id)}>{isExpanded ? 'Masquer' : 'Résultats'}</button>
                            <button type="button" onClick={() => startEdit(a)}>Modifier</button>
                            <button type="button" className="danger" onClick={() => handleDelete(a.id)} disabled={deletingId === a.id}>{deletingId === a.id ? '…' : 'Suppr.'}</button>
                          </td>
                        </tr>,
                        isExpanded && (
                          <tr key={`${a.id}-detail`} className="teacher-assignments-table-detail-row">
                            <td colSpan={7}>
                              <div className="teacher-assignments-table-detail">
                                <strong>Résultats élèves</strong>
                                {stats.studentRows?.length === 0 ? (
                                  <p>Aucun élève rattaché à cette classe.</p>
                                ) : (
                                  <ul>
                                    {(stats.studentRows || []).map((row) => (
                                      <li key={row.studentId}>
                                        {row.studentName} — {row.status === 'done' ? `Rendu • ${row.score ?? 0}%` : row.status === 'overdue' ? 'Non rendu (retard)' : 'À faire'}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      ].filter(Boolean)
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            groupedAssignments.map((grp) => (
              <section key={grp.key || 'all'} className="teacher-assignments-group">
                {grp.label && <h2 className="teacher-assignments-group-title">{grp.label}</h2>}
                <ul className="teacher-assignments-list">
                  {grp.items.map((a) => renderCard(a))}
                </ul>
              </section>
            ))
          )}

          <div className="teacher-assignments-pagination">
            <button
              type="button"
              className="teacher-assignments-pagination-btn"
              onClick={goToPrevPage}
              disabled={currentPage <= 1 || loading}
            >
              Précédent
            </button>
            <span className="teacher-assignments-pagination-info">
              {loadingCount ? '…' : totalCount != null ? `Devoirs ${fromTo} sur ${totalCount}` : ''}
              {totalPages != null && ` • Page ${currentPage} sur ${totalPages}`}
            </span>
            <button
              type="button"
              className="teacher-assignments-pagination-btn"
              onClick={goToNextPage}
              disabled={!hasMore || loading}
            >
              Suivant
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default TeacherAssignments
